/* eslint-disable import/no-anonymous-default-export */
import React, { useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FilledSquareIcon, EmptySquareIcon } from 'global/components/PerformIcons';
import { getColorFromScore } from 'global/styles';
import useStyles, { BARHEIGHT, COLORS } from './styles';
import { useView } from 'global/hooks/useView';
import { get } from '../../lib/lodash';
import clsx from 'clsx';
import { Menu } from '@mui/material';
import { EditScore } from '../../../feature/report/ChecklistScores';
import { lowerCaseAll } from '../../utils';
import { Text } from '../text';
import { Checkbox } from '../checkbox';
import { useScreenDims } from '../../styles';

const shouldSwitch = v => {
  return ['goal', 'targetGoal'].includes(v);
}

export const displayText = (score, compare, opts = {}) => {
  const compareval = get(compare, 'value');
  const _score = score.value;
  const scoreIsNull = _score == null || _score == -1 || _score == undefined;
  const compareIsNull = compareval == null || compareval == -1 || compareval == undefined;

  const switchCompare = shouldSwitch(get(opts, 'filter.assessment_status'));

  // If no good comparison, the active score displays itself, whether N/A, N/O or otherwise.
  const compareDisplay = get(compare, 'displayValue');
  const scoreDisplay = get(score, 'displayValue', get(opts, 'pssvCheckpoint.displayValue'));
  if (compareIsNull || scoreIsNull) { return <em>{switchCompare ? compareDisplay || scoreDisplay : scoreDisplay || compareDisplay}</em> }

  const diff = switchCompare ? compareval - _score : _score - compareval;
  const config = get(opts, 'scorecard.config', {});

  let disp = config.displayPercentage ? diff : diff * ((config.maxScore - config.minScore) / 100);

  if (isNaN(disp)) { return '' }

  disp = `${disp}`.length > 3 ? disp.toFixed(2) : disp;

  const pref = disp >= 0 ? '+' : '';
  const suff = config.displayPercentage ? '%' : '';

  return `${pref}${disp}${suff}`;
}

const cont = { width: 466, height: 558 }

const BarCompare = props => {
  const { score, compare } = props;
  const [state, setState] = useState({
    anchor: null,
  })

  const switchCompare = shouldSwitch(get(props, 'filter.assessment_status'));
  const { view, getView, renderConfirm, confirmState, setView } = useView();
  const { width, height } = useScreenDims();

  // Score refers to the Active value (unless swapped) -- which is the value towards which the diagram grows/shrinks
  const _score = switchCompare ? get(compare, 'value', score) : score  // Set compare to score where undefined specifically to ensure that when creating new goal scores, it displays like the existing when non-existant
  // Compare refers to the Display the user selected like Target / Draft / Review (unless Goal, where it's swapped)
  //.  which is the value the diagram positions as the baseline, the black vertical line
  const compareval = switchCompare ? score : get(compare, 'value');

  const classes = useStyles();

  const scoreIsNull = _score == null || _score == -1 || _score == undefined;
  const compareIsNull = compareval == null || compareval == -1 || compareval == undefined;

  let filtstatus = `${get(props, 'filter.assessment_status')}`;
  const isTargetGoal = filtstatus == 'targetGoal';

  if (isTargetGoal) {
    filtstatus = 'goal';
  }

  const blackBar = () => {
    return { style: { width: 2, height: BARHEIGHT + 8, backgroundColor: 'black', top: -4 } };
  }

  const generateBars = () => {
    // If there's no baseline:
    if (compareIsNull) {
      // If also no active value, it's just a empty box
      // otherwise, we show a dark grey bar representing the active value (or goal), and no blackBar
      return scoreIsNull ? [] : [{ style: { width: `${_score}%`, backgroundColor: COLORS.darkGrey } },]
    }

    // If there's nothing to compare the baseline to:
    if (scoreIsNull) {
      // we show that baseline as a vertical bar with no background
      return [{ style: { width: `${compareval}%`, backgroundColor: 'transparent' } }, blackBar()]
    }

    // Else we show both the baseline and the difference
    const val = _score - compareval;
    return [
      { style: { width: `${compareval}%`, backgroundColor: COLORS.lightGray } },
      blackBar(),
      { style: { width: `${Math.abs(val)}%`, marginLeft: val < 0 ? `calc(${val}% - 2px)` : 0, backgroundColor: val > 0 ? COLORS.green : COLORS.red } },
    ].filter(f => !!f)
  }

  const canClick = get(props, 'canClick', true);
  const onClick = (e) => {
    // console.log({ compare, compareval, props, score, val: _score - compareval, switchCompare })
    if (!canClick) {
      return;// props.onClick && props.onClick(e)
    }
    const elem = document.getElementById('ActiveAbsolute');
    setState((s) => ({ ...s, anchor: elem }));
  }

  const onClose = (afterSubmit = false) => {
    setState((s) => ({ ...s, anchor: null }));

    const statCheck = ['goal'].includes(filtstatus);

    const openTask = () => {
      props.openTaskCreate();
    }

    if (afterSubmit && statCheck && !props.isTemplate && get(props, 'assessment.data.meta.tasksEnabled') !== false) {
      const dontShow = getView().dontShowTaskOnScore;

      const updateConf = () => {
        const closeConf = () => confirmState[1]({});
        const v = getView();

        const typeLabel = props.taskTypeName || 'Task';
        confirmState[1]({
          title: `Create ${typeLabel}`,
          content: (
            <div>
              <Text>Would you like to create a {typeLabel.toLowerCase()} for this target?</Text>
              <Checkbox label="Don't show me this again." checked={v.dontShowTaskOnScore} onClick={() => {
                setView('dontShowTaskOnScore', !v.dontShowTaskOnScore)
                updateConf();
              }} />
              <Text italic>You can create a {typeLabel.toLowerCase()} later by using the dropdown next to this score.</Text>
            </div>
          ),
          okayText: `Start ${typeLabel}`,
          cancelText: 'No',
          onOk: () => {
            setView('createTaskOnScore', true);
            closeConf();

            openTask();
          },
          onCancel: () => {
            if (v.dontShowTaskOnScore) {
              setView('createTaskOnScore', false);
            }
            closeConf();
          }
        })
      }

      if (dontShow) {
        if (getView().createTaskOnScore) {
          openTask();
        }
      } else if (getView().createTaskOnScore) {
        updateConf();
      }
    }
  };

  const onUpdate = (v, fullUp, resp) => {
    return get(props, 'scores.update', () => 0)({ loading: false, resp, compare });
  }

  const addToQueue = async d => {
    props.addToQueue && await props.addToQueue(d, { onUpdate });

    const filtStat = get(props, 'filter.assessment_status');
    if (!get(props, 'assessment.data.availableStatus', []).includes(filtStat)) {
      await get(props, 'assessment.update', () => 0)({ loading: false });
    }

    onClose(true);
  }

  const hasScores = get(props, 'assessment.data.availableStatus', []).includes(filtstatus);
  const canScore = filtstatus === 'goal';
  const compareVal = get(props, `pssvCheckpoint.${['goal','target'].includes(filtstatus) ? 'target' : 'compare'}.value`)
  const activeVal = get(props, 'pssvCheckpoint.value');
  const isTemplate = !!props.isTemplate;
  const tasksEnabled = get(props, 'assessment.data.meta.tasksEnabled') !== false;

  return (
    <>
      <div onClick={onClick} className={clsx(classes.compare, canClick && !Boolean(state.anchor) && 'compareHover')}>
        <div className={'barContainer'}>
          {generateBars().map(b => <div className='bar' style={b?.style} />)}
        </div>

        <div className='text'>{displayText({ ...(props.active || {}), value: score }, compare, { ...props })}</div>
      </div>
      {Boolean(state.anchor) && 
        <Menu 
          id="scoreMenu" 
          className={classes.compareModal} 
          anchorEl={state.anchor} 
          open={Boolean(state.anchor)} 
          onClose={() => onClose()}
          anchorPosition={{ left: (width * 0.5) - (cont.width * 0.5), top: (height * 0.5) - (cont.height * 0.5) }}
          anchorReference='anchorPosition'
        >
        <div className={clsx('container', !canScore && 'noScore')}>
          <div className='header'><div>{`${canScore ? 'SET ' : ''}${(isTargetGoal || filtstatus === 'goal') ? 'NEW TARGET' : lowerCaseAll(filtstatus).toUpperCase()} SCORE`}</div></div>
          {!!canScore && <Text variant="caption">{_score ? `The highlighted option is your new target;` : `No new target is set;`} click options to change it.</Text>}
          <div className='content'>
            <EditScore 
              hideRightGrid={true} 
              setTargetStatus={filtstatus} 
              highlightActive={activeVal ?? undefined}
              highlightCompare={compareVal ?? undefined}
              {...props}
              addToQueue={addToQueue}
              canScore={canScore}
              isCompareModal
              alwaysSetComment
              pssvCheckpoint={{
                ...(props.compare || props.pssvCheckpoint),
                // Only show score as editable/selected when it's a goal ("new target")
                value: canScore && hasScores ? _score : null,
              }} 
            />
          </div>

          {canScore && !isTemplate && tasksEnabled && <div><Checkbox label="Create Tasks" checked={view.createTaskOnScore} onClick={() => setView('createTaskOnScore', !view.createTaskOnScore)} /></div>}

        </div>
      </Menu>}
      
      {renderConfirm()}
    </>
  )
}

// Expects props.score as a float between 0 and 1
// score values -1 are processed to black
export default (props) => {
  const { isBw, isAddMenu } = props;
  const classes = useStyles();
  const { isDark } = useView();

  const useCompare = get(props, 'useCompare', false);

  if (useCompare) { return <BarCompare {...props} /> }

  if (isAddMenu) {
    if (props.score === null) {
      return <AddCircleOutlineIcon style={{ color: 'dark-grey' }} />;
    }
    return <AddCircleIcon style={{ color: 'black' }} />;
  } else if (props.score === null) {
    return <EmptySquareIcon color="disabled" style={{ paddingRight: '4px' }} />;
  } else if (props.selected) {
    return <CheckBoxIcon className={props.disabled ? classes.iconDisabled : classes.icon} onClick={props.onClick} style={{ color: isDark ? 'white' : 'black' }} />;
  } else if (props.indeterminate) {
    return <IndeterminateCheckBoxIcon className={props.disabled ? classes.iconDisabled : classes.icon} onClick={props.onClick} style={{ color: isDark ? 'white' : 'black' }} />;
  }

  const sc = typeof props.score === 'string' ? parseFloat(props.score) : props.score;
  const stroke = props.useBlankCheckbox ? (isDark ? 'white' : 'black') : 'none';
  const opacity = props.useBlankCheckbox ? '0.4' : '1';

  const hasRanges = props.scorecardConfig ? [get(props, 'scorecardConfig.minScore', 0), get(props, 'scorecardConfig.maxScore', 100)] : undefined;

  return (
    <FilledSquareIcon
      className={props.disabled ? classes.iconDisabled : classes.icon}
      onClick={props.onClick}
      style={{ color: getColorFromScore(sc, hasRanges, isBw), stroke: stroke, fillOpacity: opacity, strokeWidth: 2 }}
    />
  );
};
